import React from 'react';
import {styles} from './Alert_styles.web';
import {AlertProps} from './Alert_types.web';
import {
  bgColor,
  borderColor,
  btnColor,
  iconColor,
  iconsByState,
} from './Alert_config.web';
import {useTheme} from '../../../../utils/theming/themeProvider';
import {CloseIcon} from '../../Icons/index.web';
import {ButtonColor} from '../../Buttons/Button/Button.types.web';
import Button from '../../Buttons/Button/Button.web';

const Alert = (props: React.PropsWithChildren<AlertProps>) => {
  // Props
  const {
    id,
    className,
    state,
    title,
    subTitle,
    orientation,
    primaryButton,
    secondaryButton,
    isClosable,
    align = 'left',
    containerStyle,
    iconSize = '24',
    onClose,
  } = props;

  // Styles
  const {colors, fontStyles} = useTheme();

  const generalStyles = styles();

  const LeadingIcon = iconsByState[state];
  const iconsSelfAlign =
    (((title?.trim()?.length && !subTitle?.trim()?.length) ||
      (subTitle?.trim()?.length && !title?.trim()?.length)) &&
      !primaryButton &&
      !secondaryButton) ||
    ((primaryButton || secondaryButton) && !title?.length && !subTitle?.length)
      ? 'center'
      : 'flex-start';

  return (
    <div
      id={id}
      className={className}
      style={{
        ...generalStyles.alert,
        border: `1px solid ${borderColor(colors)?.[state]}`,
        backgroundColor: bgColor(colors)?.[state],
        ...containerStyle,
      }}>
      {/* Icon */}
      {align === 'left' || orientation === 'horizontal' ? (
        <div
          style={{
            ...generalStyles.leadingIconContainer,
            height: parseInt(iconSize),
            alignSelf: iconsSelfAlign,
          }}>
          {<LeadingIcon size={iconSize} color={iconColor(colors)?.[state]} />}
        </div>
      ) : null}

      {/* Texts and buttons */}
      <div
        style={{
          ...generalStyles.mainContainer,
          flexDirection: orientation === 'vertical' ? 'column' : 'row',
        }}>
        {/* Texts */}
        {!!title?.length || !!subTitle?.length ? (
          <div
            style={{
              ...generalStyles.textsContainer,
              marginLeft:
                align === 'right' && orientation !== 'horizontal'
                  ? 'auto'
                  : undefined,
            }}>
            {!!title?.length && (
              <p
                style={{
                  ...fontStyles['bodyBoldSM'],
                  margin: 0,
                  color: colors.neutral1000,
                  textAlign: align,
                  lineHeight: !subTitle?.length
                    ? parseInt(iconSize) + 'px'
                    : fontStyles['bodyBoldSM']?.lineHeight + 'px',
                }}>
                {title}
              </p>
            )}

            {!!subTitle?.length && (
              <p
                style={{
                  ...fontStyles['bodyRegularSM'],
                  margin: 0,
                  color: colors.neutral1000,
                  textAlign: align,
                  lineHeight: !title?.length
                    ? parseInt(iconSize) + 'px'
                    : fontStyles['bodyRegularSM']?.lineHeight + 'px',
                }}>
                {subTitle}
              </p>
            )}
          </div>
        ) : null}
        {/* Buttons */}
        {primaryButton || secondaryButton ? (
          <div
            style={{
              ...generalStyles?.buttonsContainer,
              marginLeft:
                orientation === 'horizontal' || align === 'right' ? 'auto' : 0,
            }}>
            {secondaryButton ? (
              <Button
                style={'outline'}
                textSize="small"
                color={(btnColor[state] as ButtonColor) || 'blue'}
                {...secondaryButton}
              />
            ) : null}
            {primaryButton ? (
              <Button
                style={'fill'}
                textSize="small"
                color={(btnColor[state] as ButtonColor) || 'blue'}
                containerStyle={{borderWidth: 0}}
                {...primaryButton}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Icon */}
      {align === 'right' && orientation !== 'horizontal' ? (
        <div
          style={{
            ...generalStyles.leadingIconContainer,
            height: parseInt(iconSize),
            alignSelf: iconsSelfAlign,
          }}>
          {<LeadingIcon size={iconSize} color={iconColor(colors)?.[state]} />}
        </div>
      ) : null}

      {/* Close Icon*/}
      {isClosable && onClose ? (
        <div
          style={{
            ...generalStyles?.closeButton,
            alignSelf: iconsSelfAlign,
          }}
          onClick={onClose}>
          <CloseIcon size="16" color={colors.neutral1000} />
        </div>
      ) : null}
    </div>
  );
};

export default Alert;
